/**
 * Collection Filters System - Moon Moon Agency v2.1
 * Shopify collection filtering with AJAX updates, custom events, and UI sync
 */

// Initialisation de l'objet queryParams pour stocker les paramètres de requête
Shopify.queryParams = {};

/*********************************************************************************************
 * ⚙️ CONFIGURATION & CONSTANTS
 */
const COLLECTION_CONFIG = {
	// API Settings
	debounceDelay: 150,
	previewDebounceDelay: 200,
	previewCacheMax: 32,
	defaultFilterCountSection: 'filter-count',
	apiTimeout: 10000,
	
	// UI Settings
	mobileBreakpoint: window.TABLET_BREAKPOINT, // 990px — filters switch to mobile layout
	defaultOpenFilters: 1,
	
	// Price Slider Settings
	priceGap: 10,
	
	// Selectors - Using data attributes for better separation of concerns
	selectors: {
		form: '[data-collection-form]',
		grid: '[data-collection-grid]',
		gridCollection: '[data-grid-collection]',
		shopifySection: '.shopify-section', // Keep as class (Shopify generated)
		activeFilters: '[data-active-filters]',
		reloadElements: '[data-collection-reload]',
		filterSubmit: '[data-filter-submit]',
		filterCountPreview: '[data-filter-count-preview]',
		sortBy: '[data-sort-by]',
		sortByHidden: '[data-sort-by-hidden]',
		
		// Mobile filters
		mobileFilterWrapper: '[data-mobile-filters]',
		htmlElement: 'html',
		
		// Filter accordions
		filterHeaders: '[data-filter-header]',
		
		// Price sliders
		minRange: '[data-price-min]',
		maxRange: '[data-price-max]',
		sliderTrack: '[data-price-track]',
		minRangeValue: '[data-price-min-value]',
		maxRangeValue: '[data-price-max-value]',
		priceMinValue: '[data-price-min-display]',
		priceMaxValue: '[data-price-max-display]'
	},

};

/*********************************************************************************************
 * 🛠️ UTILITIES & LOGGING
 */
// Collection Event Emitter
const collectionEmit = (event, detail = {}, cancelable = false) => {
	const eventData = {
		timestamp: window.getTimestamp?.() || new Date().toLocaleString(),
		event,
		data: detail,
		source: 'collection'
	};
	
	return document.dispatchEvent(new CustomEvent(`collection:${event}`, {
		detail: eventData,
		cancelable,
		bubbles: true
	}));
};

// DOM Selectors Cache
const CollectionDOM = {
	get form() { return document.querySelector(COLLECTION_CONFIG.selectors.form); },
	get grid() { return document.querySelector(COLLECTION_CONFIG.selectors.grid); },
	get gridCollection() { return document.querySelector(COLLECTION_CONFIG.selectors.gridCollection); },
	get sortBySelector() { return document.querySelector(COLLECTION_CONFIG.selectors.sortBy); },
	get sortByHidden() { return document.querySelector(COLLECTION_CONFIG.selectors.sortByHidden); },
	get html() { return document.querySelector(COLLECTION_CONFIG.selectors.htmlElement); },
	get mobileFilterWrapper() { return document.querySelector(COLLECTION_CONFIG.selectors.mobileFilterWrapper); },
	
	get activeFilters() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters); },
	get reloadElements() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements); },
	get filterHeaders() { return document.querySelectorAll(COLLECTION_CONFIG.selectors.filterHeaders); },
	get formInputs() { return this.form?.querySelectorAll('input') || []; },
	
	// Price sliders
	get priceSliders() {
		return {
			minRange: document.querySelector(COLLECTION_CONFIG.selectors.minRange),
			maxRange: document.querySelector(COLLECTION_CONFIG.selectors.maxRange),
			sliderTrack: document.querySelector(COLLECTION_CONFIG.selectors.sliderTrack),
			minRangeValue: document.querySelector(COLLECTION_CONFIG.selectors.minRangeValue),
			maxRangeValue: document.querySelector(COLLECTION_CONFIG.selectors.maxRangeValue)
		};
	},
	
	get shopifySection() {
		const gridElem = this.gridCollection;
		return gridElem?.closest(COLLECTION_CONFIG.selectors.shopifySection);
	},

	get filterSubmitBtn() {
		return document.querySelector(COLLECTION_CONFIG.selectors.filterSubmit);
	},

	get filterCountSectionId() {
		const id = this.gridCollection?.dataset?.filterCountSection;
		return id || COLLECTION_CONFIG.defaultFilterCountSection;
	},
};

/*********************************************************************************************
 * 🔌 CORE COLLECTION API
 */
const CollectionAPI = {
	async request(url, signal) {
		collectionEmit('api-start', { url });

		try {
			const response = await fetch(url, signal ? { signal } : undefined);
			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			// Check if response is JSON (Section Rendering API) or HTML (fallback)
			const contentType = response.headers.get('content-type');
			const data = contentType && contentType.includes('application/json')
				? await response.json()
				: await response.text();

			collectionEmit('api-success', { url, responseType: typeof data });
			return data;
		} catch (error) {
			collectionEmit('api-error', { url, error: error.message });
			throw error;
		}
	},

	getSectionId() {
		const section = CollectionDOM.shopifySection;
		if (!section) {
			throw new Error('Collection grid element or shopify section not found');
		}
		return section.id;
	},

	getSectionIdShort() {
		return this.getSectionId().replace('shopify-section-', '');
	},

	extractSectionHtml(response, sectionId) {
		if (typeof response === 'object' && response?.[sectionId]) {
			return response[sectionId];
		}
		if (typeof response === 'string') {
			return response;
		}
		return null;
	},

	updateFilterCtaLabel(label) {
		if (!label) return;
		const currentCta = CollectionDOM.filterSubmitBtn;
		const currentSpan = currentCta?.querySelector('span');
		if (currentSpan) {
			currentSpan.textContent = label;
		}
	},

	updateFilterCtaFromSectionHtml(sectionHtml) {
		if (!sectionHtml) return;

		const tempEl = document.createElement('DIV');
		tempEl.innerHTML = sectionHtml;

		const preview = tempEl.querySelector(COLLECTION_CONFIG.selectors.filterCountPreview);
		if (preview?.textContent?.trim()) {
			this.updateFilterCtaLabel(preview.textContent.trim());
			return;
		}

		const newCta = tempEl.querySelector(COLLECTION_CONFIG.selectors.filterSubmit);
		const currentCta = CollectionDOM.filterSubmitBtn;
		if (!newCta || !currentCta) return;

		const newSpan = newCta.querySelector('span');
		const currentSpan = currentCta.querySelector('span');
		if (currentSpan && newSpan) {
			currentSpan.textContent = newSpan.textContent;
		}
	},

	async refreshWithFilters(url_filters, signal) {
		collectionEmit('filter-start', { filters: url_filters });

		try {
			const currentUrl = `${window.location.pathname}`;
			const sectionId = this.getSectionIdShort();

			// Section Rendering API
			const requestUrl = `${currentUrl}?${url_filters}&sections=${sectionId}`;
			const response = await this.request(requestUrl, signal);
			
			if (!CollectionDOM.grid) {
				throw new Error('Collection grid element not found');
			}

			let newSectionHtml = this.extractSectionHtml(response, sectionId);
			if (!newSectionHtml && typeof response === 'string') {
				const newPageEl = document.createElement('DIV');
				newPageEl.innerHTML = response;
				const newSection = newPageEl.querySelector(COLLECTION_CONFIG.selectors.grid);
				if (!newSection) {
					throw new Error('Required collection elements not found in response');
				}
				newSectionHtml = newSection.outerHTML;
			}
			if (!newSectionHtml) {
				throw new Error('Invalid response format');
			}

			this.updateDOM(newSectionHtml, currentUrl, url_filters);
			collectionEmit('filter-success', { filters: url_filters, url: `${currentUrl}?${url_filters}` });
			
		} catch (error) {
			if (error?.name === 'AbortError') return;
			collectionEmit('filter-error', { error: error.message, filters: url_filters });

			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-co-grid--loading');
			}

			// Fallback: reload page if AJAX fails
			window.location.search = url_filters;
		}
	},

	updateDOM(newSectionHtml, currentUrl, url_filters) {
		// Create temporary element to parse new HTML
		const tempEl = document.createElement('DIV');
		tempEl.innerHTML = newSectionHtml;

		// Update collection grid content (products only)
		const newGridCollection = tempEl.querySelector(COLLECTION_CONFIG.selectors.grid);
		if (newGridCollection && CollectionDOM.grid) {
			try {
				CollectionDOM.grid.innerHTML = newGridCollection.innerHTML;
			} catch (e) {
				window.log('warn', '🔲 [COLLECTION]', 'Grid DOM update partial error (third-party)', { error: e.message });
			}
		}

		// Update active filters
		const newActiveFilters = tempEl.querySelectorAll(COLLECTION_CONFIG.selectors.activeFilters);
		CollectionDOM.activeFilters.forEach((currentFilter, index) => {
			if (newActiveFilters[index]) {
				currentFilter.innerHTML = newActiveFilters[index].innerHTML;
			}
		});

		// Update other elements with 'data-collection-reload' attribute
		// Use data-reload-id for stable matching; fall back to index-based for elements without it
		const newReloadElems = tempEl.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements);
		const newReloadById = {};
		newReloadElems.forEach(el => {
			const id = el.dataset.reloadId;
			if (id) newReloadById[id] = el;
		});

		CollectionDOM.reloadElements.forEach((currentElem, index) => {
			const id = currentElem.dataset.reloadId;
			const newElem = id ? newReloadById[id] : newReloadElems[index];
			if (!newElem) return;

			// Pagination : remplacement COMPLET + rebind, comme CollectionLoadMore._applyResponse.
			// La logique span-only ci-dessous ne copiait que le libellé du bouton (1er <span>) :
			// après un changement de filtre, le compteur "X sur Y" gardait l'ancien total et
			// data-next-url restait périmé (le load-more aurait chargé la mauvaise page).
			if (id === 'pagination') {
				currentElem.innerHTML = newElem.innerHTML;
				const newBtn = currentElem.querySelector('[data-load-more-btn]');
				if (newBtn) CollectionLoadMore.bindBtn(newBtn);
				return;
			}

			const currentSpan = currentElem.querySelector('span');
			const newSpan = newElem.querySelector('span');

			if (currentSpan && newSpan) {
				currentSpan.textContent = newSpan.textContent;
			} else {
				currentElem.innerHTML = newElem.innerHTML;
			}
		});

		// Update URL in history without reloading page
		const newUrl = `${currentUrl}?${url_filters}`;
		window.history.replaceState(null, document.title, newUrl);
		
		if (CollectionDOM.grid) {
			CollectionDOM.grid.classList.remove('mm-co-grid--loading');
		}
		
		// Filter form / color strip are NOT replaced here (only grid.innerHTML + reload targets),
		// so their listeners stay valid — no need to re-init CollectionFilters / ColorGroupFilters / ColorStripToggle.
		// GridToggle re-applies the saved column count to the freshly rendered grid.
		GridToggle.init();
		
		// Re-initialize product cards (swatches, image nav) after DOM update
		if (typeof window.ProductCardManager !== 'undefined') {
			window.ProductCardManager.init();
		}

		// Re-apply color strip selection to newly loaded cards
		if (typeof window.ColorStripSync !== 'undefined') {
			window.ColorStripSync.restoreState();
		}

		// Re-initialize add to cart forms after DOM update
		if (typeof window.initAddToCartForms === 'function') {
			window.initAddToCartForms();
		}

		if (typeof window.mmInitButtonAnimateChars === 'function') {
			window.mmInitButtonAnimateChars(CollectionDOM.gridCollection);
		}
	}
};

/*********************************************************************************************
 * 🔍 COLLECTION FILTERS MANAGEMENT
 */
const CollectionFilters = {
	_previewTimer: null,
	_previewAbort: null,
	_previewRequestId: 0,
	_previewCache: new Map(),

	init() {
		if (!CollectionDOM.form) {
			collectionEmit('form-error', { error: 'Collection facets form not found' });
			return;
		}

		this._boundHandleInputChange = this._boundHandleInputChange || this.handleInputChange.bind(this);

		const inputs = CollectionDOM.formInputs;

		inputs.forEach(input => {
			input.removeEventListener('change', this._boundHandleInputChange);
			input.addEventListener('change', this._boundHandleInputChange);
		});

		this._updateFilterCount();
		collectionEmit('filters-initialized', { inputCount: inputs.length });
	},

	_updateFilterCount() {
		const badge = document.querySelector('[data-filter-count]');
		if (!badge) return;
		const form = CollectionDOM.form;
		if (!form) return;
		let count = 0;
		form.querySelectorAll('input[type="checkbox"]:checked, input[type="radio"]:checked').forEach(() => count++);
		if (count > 0) {
			badge.textContent = `(${count})`;
			badge.classList.remove('mm-none');
			badge.removeAttribute('aria-hidden');
		} else {
			badge.textContent = '';
			badge.classList.add('mm-none');
			badge.setAttribute('aria-hidden', 'true');
		}
	},

	_cachePreviewLabel(url_filters, label) {
		if (!label) return;
		if (this._previewCache.size >= COLLECTION_CONFIG.previewCacheMax) {
			const oldest = this._previewCache.keys().next().value;
			this._previewCache.delete(oldest);
		}
		this._previewCache.set(url_filters, label);
	},

	// Les 2 inputs range du filtre prix sont DANS le formulaire : ils sont donc sérialisés à
	// chaque soumission, même quand l'utilisateur n'a jamais touché au slider (un simple clic
	// sur une pastille couleur suffisait à injecter ?price.gte=0&price.lte=90 dans l'URL).
	// Une fourchette qui couvre toute la bande de prix n'est pas un filtre : on la retire.
	// Sans ça, une vue filtrée où range_max tombe à 0 rendait le slider en max="0", et la
	// soumission suivante produisait price.gte=0&price.lte=0 — résultat vide et sans issue.
	stripFullRangePrice(params) {
		const min = CollectionDOM.form?.querySelector(COLLECTION_CONFIG.selectors.minRange);
		const max = CollectionDOM.form?.querySelector(COLLECTION_CONFIG.selectors.maxRange);
		if (!min || !max) return params;
		const atFullRange = Number(min.value) <= Number(min.min) && Number(max.value) >= Number(max.max);
		if (atFullRange) {
			params.delete(min.name);
			params.delete(max.name);
		}
		return params;
	},

	// Le select de tri est hors du <form> : FormData ne le sérialise pas, et chaque filtre
	// AJAX retirait sort_by de l'URL (tri perdu). On reprend celui de l'URL courante.
	keepSort(params) {
		const sort = new URLSearchParams(location.search).get('sort_by');
		if (sort) params.set('sort_by', sort);
		return params;
	},

	getFormFilterParams() {
		if (!CollectionDOM.form) return '';
		return this.keepSort(this.stripFullRangePrice(new URLSearchParams(new FormData(CollectionDOM.form)))).toString();
	},

	schedulePreviewCount() {
		clearTimeout(this._previewTimer);
		this._previewTimer = setTimeout(() => {
			void this.previewProductCount();
		}, COLLECTION_CONFIG.previewDebounceDelay);
	},

	// Annule un preview count programmé ou en vol (utilisé quand on applique
	// directement le filtre, ex. clic couleur — la requête preview serait redondante).
	cancelPreview() {
		clearTimeout(this._previewTimer);
		if (this._previewAbort) {
			this._previewAbort.abort();
			this._previewAbort = null;
		}
	},

	async previewProductCount() {
		if (!CollectionDOM.form || !CollectionDOM.filterSubmitBtn) return;

		const url_filters = this.getFormFilterParams();
		const cachedLabel = this._previewCache.get(url_filters);
		if (cachedLabel) {
			CollectionAPI.updateFilterCtaLabel(cachedLabel);
			return;
		}

		// The CTA label is computed server-side by the main section (the standalone count section
		// only renders via ?sections= when it's part of the template, which it isn't — it returns null).
		// We request both: the lightweight count section if it ever becomes template-rendered, and the
		// main section as the reliable source of the recomputed label.
		const countSectionId = CollectionDOM.filterCountSectionId;
		const mainSectionId = CollectionAPI.getSectionIdShort();
		const sectionsParam = countSectionId !== mainSectionId
			? `${encodeURIComponent(countSectionId)},${encodeURIComponent(mainSectionId)}`
			: encodeURIComponent(mainSectionId);
		const requestUrl = `${window.location.pathname}?${url_filters}&sections=${sectionsParam}`;
		const requestId = ++this._previewRequestId;

		if (this._previewAbort) {
			this._previewAbort.abort();
		}
		this._previewAbort = new AbortController();

		const cta = CollectionDOM.filterSubmitBtn;
		cta?.setAttribute('aria-busy', 'true');

		collectionEmit('filter-preview-start', { filters: url_filters });

		try {
			const response = await fetch(requestUrl, { signal: this._previewAbort.signal });
			if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

			const data = await response.json();
			if (requestId !== this._previewRequestId) return;

			const sectionHtml = data[countSectionId] || data[mainSectionId];
			if (!sectionHtml) throw new Error('Section not found in response');

			const tempEl = document.createElement('DIV');
			tempEl.innerHTML = sectionHtml;

			const preview = tempEl.querySelector(COLLECTION_CONFIG.selectors.filterCountPreview);
			const ctaBtn = !preview ? tempEl.querySelector(COLLECTION_CONFIG.selectors.filterSubmit) : null;
			const label = preview?.textContent?.trim() || ctaBtn?.querySelector('span')?.textContent?.trim();

			if (label) {
				this._cachePreviewLabel(url_filters, label);
				CollectionAPI.updateFilterCtaLabel(label);
				collectionEmit('filter-preview-success', { filters: url_filters });
			}
		} catch (error) {
			if (error.name === 'AbortError') return;
			collectionEmit('filter-preview-error', { error: error.message });
		} finally {
			if (requestId === this._previewRequestId) {
				cta?.removeAttribute('aria-busy');
			}
		}
	},

	handleInputChange(event) {
		collectionEmit('input-change', {
			name: event.target.name || event.target.id,
			value: event.target.value,
			checked: event.target.checked,
		});
		this._updateFilterCount();
		this.schedulePreviewCount();
	},

	async submitForm() {
		if (!CollectionDOM.form || !CollectionDOM.grid) {
			collectionEmit('form-error', { error: 'Required elements not found' });
			return;
		}
		
		try {
			collectionEmit('form-submit-start');

			clearTimeout(this._previewTimer);
			if (this._previewAbort) {
				this._previewAbort.abort();
			}
			this._previewCache.clear();
			
			const url_filters = this.getFormFilterParams();
			
			CollectionDOM.grid.classList.add('mm-co-grid--loading');
			
			await CollectionAPI.refreshWithFilters(url_filters);

			MobileFilters.close();
			collectionEmit('form-submit-success', { filters: url_filters });
			
		} catch (error) {
			collectionEmit('form-submit-error', { error: error.message });
			
			if (CollectionDOM.grid) {
				CollectionDOM.grid.classList.remove('mm-co-grid--loading');
			}
		}
	}
};

/*********************************************************************************************
 * 📱 FILTER DRAWER (all screen sizes)
 */
const MobileFilters = {
	open() {
		const wrapperMobile = CollectionDOM.mobileFilterWrapper;
		const html = CollectionDOM.html;

		if (wrapperMobile && html) {
			wrapperMobile.classList.add('mm-open');
			html.classList.add('stop-scrolling');
			window.LenisManager?.stop();
			collectionEmit('filter-drawer-opened');
		}
	},

	close() {
		const wrapperMobile = CollectionDOM.mobileFilterWrapper;
		const html = CollectionDOM.html;

		if (wrapperMobile && html) {
			wrapperMobile.classList.remove('mm-open');
			html.classList.remove('stop-scrolling');
			window.LenisManager?.resume();
			collectionEmit('filter-drawer-closed');
		}
	},

	checkDrawer() {
		const hasOpenFilters = document.location.href.includes('open_filters');
		if (hasOpenFilters) this.open();
	}
};

/*********************************************************************************************
 * 🎛️ FILTER ACCORDIONS
 */
const FilterAccordions = {
	init() {
		const filtersHeaders = CollectionDOM.filterHeaders;

		filtersHeaders.forEach((filtersHeader, index) => {
			// Activate first three filters by default
			if (index < COLLECTION_CONFIG.defaultOpenFilters) {
				filtersHeader.classList.add('mm-active');
			}
			// Sync ARIA initial state (markup defaults to aria-expanded="false")
			filtersHeader.setAttribute('aria-expanded', String(filtersHeader.classList.contains('mm-active')));

			const toggle = () => {
				const active = filtersHeader.classList.toggle('mm-active');
				filtersHeader.setAttribute('aria-expanded', String(active));
				collectionEmit('filter-accordion-toggle', { index, active });
			};

			// Add click event to open/close filters
			filtersHeader.addEventListener('click', toggle);

			// Keyboard support (role="button" requires manual Enter/Space handling)
			filtersHeader.addEventListener('keydown', function(e) {
				if (e.key === 'Enter' || e.key === ' ') {
					e.preventDefault();
					toggle();
				}
			});
		});
		
		collectionEmit('accordions-initialized', { count: filtersHeaders.length });
	}
};

/*********************************************************************************************
 * 💰 PRICE SLIDERS
 */
const PriceSliders = {
	get elements() {
		return CollectionDOM.priceSliders;
	},
	
	isReady() {
		const elements = this.elements;
		return elements.minRange && elements.maxRange && 
				elements.sliderTrack && elements.minRangeValue && 
				elements.maxRangeValue;
	},

	init() {
		if (!this.isReady()) return;
		
		const { minRange, maxRange } = this.elements;
		
		minRange.addEventListener('input', () => {
			if (parseInt(minRange.value) > parseInt(maxRange.value) - COLLECTION_CONFIG.priceGap) {
				minRange.value = maxRange.value - COLLECTION_CONFIG.priceGap;
			}
			this.updateRange();
		});

		maxRange.addEventListener('input', () => {
			if (parseInt(maxRange.value) < parseInt(minRange.value) + COLLECTION_CONFIG.priceGap) {
				maxRange.value = parseInt(minRange.value) + COLLECTION_CONFIG.priceGap;
			}
			this.updateRange();
		});
		
		// Initial update
		this.updateRange();
		collectionEmit('price-sliders-initialized');
	},

	updateRange() {
		if (!this.isReady()) {
			collectionEmit('price-error', { error: 'Price slider elements not found' });
			return;
		}
		
		const { minRange, maxRange, sliderTrack, minRangeValue, maxRangeValue } = this.elements;
		
		const minValue = parseInt(minRange.value, 10); 
		const maxValue = parseInt(maxRange.value, 10);

		// Update min and max values display
		const minValueEl = minRangeValue.querySelector(COLLECTION_CONFIG.selectors.priceMinValue);
		const maxValueEl = maxRangeValue.querySelector(COLLECTION_CONFIG.selectors.priceMaxValue);
		
		if (minValueEl) minValueEl.innerHTML = minValue;
		if (maxValueEl) maxValueEl.innerHTML = maxValue;
		
		// Calculate percentages for track background
		const minPercent = (minValue / minRange.max) * 100;
		const maxPercent = (maxValue / maxRange.max) * 100;

		// Update track background to reflect selected price range
		sliderTrack.style.background = `linear-gradient(to right, #ddd ${minPercent}%, var(--black-900) ${minPercent}%, var(--black-900) ${maxPercent}%, #ddd ${maxPercent}%)`;

		collectionEmit('price-update', { minValue, maxValue, minPercent, maxPercent });
	}
};

/*********************************************************************************************
 * 🎨 COLOR GROUP FILTERS
 */
const ColorGroupFilters = {
	_debounceTimer: null,
	_abortController: null,

	// Les 2 rendus d'une même couleur : chip du strip toolbar (MM-Main-Collection-Grid) et
	// swatch du drawer de filtres (mm-filter). Classes distinctes, mais même data-group-handles
	// → sélecteur commun obligatoire, sinon le drawer n'écoute rien et ne synchronise pas.
	_selector: '.mm-cf-color-group-checkbox, .mm-cf-color-swatch-checkbox',

	init() {
		const groupCheckboxes = document.querySelectorAll(this._selector);
		if (!groupCheckboxes.length) return;

		// Bind créé UNE fois : réassigné dans la boucle, removeEventListener ne retirait jamais
		// le handler précédent (référence déjà remplacée) → listeners empilés à chaque re-render.
		this._boundHandleChange ||= this._handleChange.bind(this);

		groupCheckboxes.forEach(checkbox => {
			checkbox.removeEventListener('change', this._boundHandleChange);
			checkbox.addEventListener('change', this._boundHandleChange);
		});

		this._updateColorCount();
		collectionEmit('color-groups-initialized', { count: groupCheckboxes.length });
	},

	_updateColorCount() {
		const badge = document.querySelector('[data-color-count]');
		if (!badge) return;
		// Count unique checked chips in the toolbar strip only (avoid double-counting drawer duplicates)
		const strip = document.querySelector('[data-color-strip]');
		const count = strip
			? strip.querySelectorAll('.mm-co-color-chip:checked').length
			: document.querySelectorAll('.mm-co-color-chip:checked').length;
		if (count > 0) {
			badge.textContent = `(${count})`;
			badge.classList.remove('mm-none');
			badge.removeAttribute('aria-hidden');
		} else {
			badge.textContent = '';
			badge.classList.add('mm-none');
			badge.setAttribute('aria-hidden', 'true');
		}
	},

	_handleChange(e) {
		const checkbox = e.target;
		const handles = checkbox.dataset.groupHandles || '';
		const handleList = handles ? handles.split('|') : [];
		const isChecked = checkbox.checked;

		// Sync sibling group checkboxes (toolbar ↔ filter panel)
		document.querySelectorAll(this._selector).forEach(cb => {
			if (cb !== checkbox && cb.dataset.groupHandles === handles) {
				cb.checked = isChecked;
			}
		});

		this._updateColorCount();

		// Notify product cards to update their displayed image
		document.dispatchEvent(new CustomEvent('collection:color-strip-changed', {
			detail: { handleList, isChecked },
			bubbles: true
		}));

		// Get the filter param name from the color strip
		const strip = document.querySelector('[data-color-strip]');
		const filterParam = strip?.dataset.filterParam;

		if (!filterParam) {
			CollectionFilters.submitForm();
			return;
		}

		// Snapshot checked state now (before debounce delay)
		const form = document.querySelector('[data-collection-form]');
		let params = form ? new URLSearchParams(new FormData(form)) : new URLSearchParams();
		// Idem submitForm : ne pas propager une fourchette de prix non filtrante.
		params = CollectionFilters.keepSort(CollectionFilters.stripFullRangePrice(params));
		params.delete(filterParam);
		document.querySelectorAll('.mm-co-color-chip').forEach(chip => {
			if (!chip.checked) return;
			const chipValues = (chip.dataset.groupValues || chip.dataset.groupHandles || '').split('|');
			chipValues.forEach(v => { if (v) params.append(filterParam, v); });
		});
		const paramsString = params.toString();

		// Cancel any in-flight request and pending debounce
		clearTimeout(this._debounceTimer);
		if (this._abortController) {
			this._abortController.abort();
			this._abortController = null;
		}

		// Annuler le preview count en vol : on applique le filtre directement,
		// la 2e requête (label CTA) serait redondante. Évite le double fetch couleur.
		CollectionFilters.cancelPreview();

		CollectionDOM.grid?.classList.add('mm-co-grid--loading');

		this._debounceTimer = setTimeout(async () => {
			this._abortController = new AbortController();
			try {
				await CollectionAPI.refreshWithFilters(paramsString, this._abortController.signal);
			} catch (err) {
				if (err?.name !== 'AbortError') throw err;
			} finally {
				this._abortController = null;
			}
		}, COLLECTION_CONFIG.debounceDelay);
	}
};

/*********************************************************************************************
 * 🔄 SORT BY FUNCTIONALITY
 */
const SortBy = {
	init() {
		const sortBySelector = CollectionDOM.sortBySelector;
		if (!sortBySelector) {
			collectionEmit('sort-error', { error: 'Sort by selector not found' });
			return;
		}

		sortBySelector.addEventListener('change', (e) => {
			// Repartir de l'URL courante (tenue à jour par updateDOM via replaceState) :
			// Shopify.queryParams n'est jamais rempli sur les collections, le tri effaçait tous les filtres.
			const value = e.target.value;
			const params = new URLSearchParams(location.search);
			collectionEmit('sort-change', { value, previousValue: params.get('sort_by') });
			params.set('sort_by', value);
			params.delete('page');
			location.search = params.toString();
		});

		collectionEmit('sort-initialized', { currentValue: sortBySelector.value });
	}
};

/*********************************************************************************************
 * 🖼️ BREAKING BLOCK PARALLAX
 */
const BreakingBlockParallax = {
	_triggers: [],

	init() {
		if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') return;

		const wrappers = document.querySelectorAll('[data-bb-parallax]');
		if (!wrappers.length) return;

		wrappers.forEach((wrap) => {
			const img = wrap.querySelector('img');
			if (!img) return;

			const tween = gsap.fromTo(img,
				{ yPercent: -7.5 },
				{
					yPercent: 7.5,
					ease: 'none',
					scrollTrigger: {
						trigger: wrap,
						start: 'top bottom',
						end: 'bottom top',
						scrub: true,
					},
				}
			);
			this._triggers.push(tween);
		});
	},

	cleanup() {
		this._triggers.forEach((t) => t.scrollTrigger?.kill());
		this._triggers = [];
	},
};

/*********************************************************************************************
 * ➕ LOAD MORE (pagination grille collection)
 */
const CollectionLoadMore = {
	/* Attache le listener directement sur le bouton (comme Jolly Mama)
	   — évite le scroll natif Chrome déclenché par les listeners délégués sur document */
	bindBtn(btn) {
		if (!btn || btn._loadMoreBound) return;
		btn._loadMoreBound = true;

		btn.addEventListener('click', async () => {
			const nextUrl = btn.dataset.nextUrl;
			if (!nextUrl || btn.dataset.loading === 'true') return;

			if (!CollectionDOM.shopifySection || !CollectionDOM.grid) {
				window.location.href = nextUrl;
				return;
			}

			const sectionId = CollectionAPI.getSectionIdShort();
			const sep = nextUrl.includes('?') ? '&' : '?';
			const requestUrl = `${nextUrl}${sep}sections=${sectionId}`;

			btn.dataset.loading = 'true';
			btn.style.opacity = '0.5';
			CollectionDOM.grid?.classList.add('mm-co-grid--loading');

			try {
				let response;
				for (let attempt = 0; attempt <= 2; attempt++) {
					try {
						response = await CollectionAPI.request(requestUrl);
						break;
					} catch (err) {
						if (err.message.includes('429') && attempt < 2) {
							await new Promise(r => setTimeout(r, 1000 * (attempt + 1)));
						} else {
							throw err;
						}
					}
				}
				this._applyResponse(response, sectionId);
				collectionEmit('load-more-success', { url: nextUrl });
			} catch (error) {
				collectionEmit('load-more-error', { error: error.message });
				window.log('error', 'Collection', 'Load more failed', { error: error.message });
				btn.style.opacity = '1';
			} finally {
				delete btn.dataset.loading;
				CollectionDOM.grid?.classList.remove('mm-co-grid--loading');
			}
		});
	},

	init() {
		const btn = document.querySelector('[data-load-more-btn]');
		if (btn) this.bindBtn(btn);
	},

	_applyResponse(response, sectionId) {
		const sectionHtml = CollectionAPI.extractSectionHtml(response, sectionId);
		if (!sectionHtml) {
			throw new Error('Invalid load-more response');
		}

		const tempEl = document.createElement('div');
		tempEl.innerHTML = sectionHtml;

		const newGrid = tempEl.querySelector(COLLECTION_CONFIG.selectors.grid);
		const currentGrid = CollectionDOM.grid;
		if (newGrid && currentGrid) {
			while (newGrid.firstChild) {
				currentGrid.appendChild(newGrid.firstChild);
			}
		}

		const newReloadElems = tempEl.querySelectorAll(COLLECTION_CONFIG.selectors.reloadElements);
		const newReloadById = {};
		newReloadElems.forEach(el => {
			const id = el.dataset.reloadId;
			if (id) newReloadById[id] = el;
		});

		CollectionDOM.reloadElements.forEach((currentElem, index) => {
			const id = currentElem.dataset.reloadId;
			const newElem = id ? newReloadById[id] : newReloadElems[index];
			if (!newElem) return;

			if (id === 'pagination') {
				currentElem.innerHTML = newElem.innerHTML;
				/* Réattacher le listener sur le nouveau bouton injecté */
				const newBtn = currentElem.querySelector('[data-load-more-btn]');
				if (newBtn) this.bindBtn(newBtn);
				return;
			}

			const currentSpan = currentElem.querySelector('span');
			const newSpan = newElem.querySelector('span');
			if (currentSpan && newSpan) {
				currentSpan.textContent = newSpan.textContent;
			} else {
				currentElem.innerHTML = newElem.innerHTML;
			}
		});

		// Load-more only appends cards + swaps the pagination block — the filter form / color strip
		// are untouched, so their listeners stay valid. Only GridToggle needs to re-apply the column count.
		GridToggle.init();
		/* BreakingBlockParallax omis : ScrollTrigger.refresh() interne décale le scroll Lenis */

		if (typeof window.ProductCardManager !== 'undefined') {
			window.ProductCardManager.init();
		}
		if (typeof window.ColorStripSync !== 'undefined') {
			window.ColorStripSync.restoreState();
		}
		if (typeof window.initAddToCartForms === 'function') {
			window.initAddToCartForms();
		}
		if (typeof window.mmInitButtonAnimateChars === 'function') {
			window.mmInitButtonAnimateChars(CollectionDOM.gridCollection);
		}
	},
};

/*********************************************************************************************
 * 🔽 SORT DROPDOWN (custom checkbox UI)
 */
const SortDropdown = {
	_initialized: false,
	_activeTrigger: null,

	init() {
		if (this._initialized) return;
		this._initialized = true;

		/* Toggle open/close on trigger click */
		document.addEventListener('click', (e) => {
			const trigger = e.target.closest('[data-sort-trigger]');
			const dropdown = e.target.closest('[data-sort-dropdown]');

			/* Close if click outside */
			if (!dropdown) {
				this._closeAll();
				return;
			}

			if (trigger) {
				const isOpen = trigger.getAttribute('aria-expanded') === 'true';
				this._closeAll();
				if (!isOpen) this._open(trigger);
				return;
			}

			/* Option click */
			const option = e.target.closest('[data-sort-value]');
			if (option) {
				const wrapper = option.closest('[data-sort-dropdown]');
				this._select(wrapper, option.dataset.sortValue);
			}
		});

		/* Keyboard navigation */
		document.addEventListener('keydown', (e) => {
			if (e.key === 'Escape') {
				this._closeAll(true);
				return;
			}

			const option = e.target.closest('[role="option"]');
			if (!option) return;
			const wrapper = option.closest('[data-sort-dropdown]');
			if (!wrapper) return;
			const listbox = wrapper.querySelector('[role="listbox"]');
			const options = Array.from(listbox.querySelectorAll('[role="option"]'));
			const index = options.indexOf(option);

			switch (e.key) {
				case 'ArrowDown':
					e.preventDefault();
					options[(index + 1) % options.length].focus();
					break;
				case 'ArrowUp':
					e.preventDefault();
					options[(index - 1 + options.length) % options.length].focus();
					break;
				case 'Home':
					e.preventDefault();
					options[0].focus();
					break;
				case 'End':
					e.preventDefault();
					options[options.length - 1].focus();
					break;
				case 'Enter':
				case ' ':
					e.preventDefault();
					this._select(wrapper, option.dataset.sortValue);
					break;
			}
		});
	},

	_open(trigger) {
		trigger.setAttribute('aria-expanded', 'true');
		this._activeTrigger = trigger;
		const wrapper = trigger.closest('[data-sort-dropdown]');
		if (!wrapper) return;
		const listbox = wrapper.querySelector('[role="listbox"]');
		if (!listbox) return;
		const focused = listbox.querySelector('[aria-selected="true"]') || listbox.querySelector('[role="option"]');
		if (focused) focused.focus();
	},

	_closeAll(returnFocus = false) {
		document.querySelectorAll('[data-sort-trigger]').forEach(t => {
			t.setAttribute('aria-expanded', 'false');
		});
		if (returnFocus && this._activeTrigger) {
			this._activeTrigger.focus();
		}
		this._activeTrigger = null;
	},

	_select(wrapper, value) {
		/* Update aria-selected on options */
		wrapper.querySelectorAll('[data-sort-value]').forEach(opt => {
			opt.setAttribute('aria-selected', String(opt.dataset.sortValue === value));
		});

		/* Drive the hidden native select + trigger navigation */
		const hiddenSelect = wrapper.querySelector('[data-sort-by]');
		if (hiddenSelect) {
			hiddenSelect.value = value;
			hiddenSelect.dispatchEvent(new Event('change', { bubbles: true }));
		}

		this._closeAll(true);
	}
};

/*********************************************************************************************
 * 🎨 COLOR STRIP TOGGLE
 */
const ColorStripToggle = {
	_initialized: false,

	init() {
		if (this._initialized) return;
		this._initialized = true;

		document.addEventListener('click', (e) => {
			const btn = e.target.closest('[data-color-strip-toggle]');
			if (!btn) return;

			const strip = document.querySelector('[data-color-strip]');
			if (!strip) return;

			const isOpen = strip.classList.contains('mm-active');
			strip.classList.toggle('mm-active', !isOpen);
			btn.setAttribute('aria-expanded', String(!isOpen));
			strip.setAttribute('aria-hidden', String(isOpen));
		});
	},

	// Re-open strip after filter reload if it was open
	restoreState() {
		const btn = document.querySelector('[data-color-strip-toggle]');
		const strip = document.querySelector('[data-color-strip]');
		if (!btn || !strip) return;

		if (btn.getAttribute('aria-expanded') === 'true') {
			strip.classList.add('mm-active');
			strip.setAttribute('aria-hidden', 'false');
		}
	}
};

/*********************************************************************************************
 * 🔲 GRID TOGGLE
 */
const GridToggle = {
	config: {
		storageKey: 'mm-grid-cols-v2',
		defaultCols: 7,
	},

	init() {
		const buttons = document.querySelectorAll('[data-grid-toggle]');
		if (!buttons.length) return;

		// Restore saved preference (v2 key avoids stale values from old grid config)
		// PERF : application MUETTE (pas de FLIP). init() est rappelé après chaque
		// filtre/load-more (cf. L303, L898) ; animer la grille entière à chaque
		// re-render = 2×N getBoundingClientRect inutiles. Le FLIP n'a de sens qu'au
		// clic utilisateur (_handleClick).
		const saved = localStorage.getItem(this.config.storageKey);
		const validCols = [7, 4];
		if (saved && validCols.includes(parseInt(saved))) this._applySilent(parseInt(saved));

		buttons.forEach(btn => {
			btn.removeEventListener('click', this._boundClick);
			this._boundClick = this._handleClick.bind(this);
			btn.addEventListener('click', this._boundClick);
		});
	},

	_handleClick(e) {
		const cols = parseInt(e.currentTarget.dataset.gridToggle);
		this._apply(cols);
		localStorage.setItem(this.config.storageKey, cols);
	},

	// Applique la colonne sans animation (re-render filtre/load-more, restauration init).
	_applySilent(cols) {
		const grid = document.querySelector('[data-collection-grid]');
		if (grid) grid.dataset.gridCols = cols;
		document.querySelectorAll('[data-grid-toggle]').forEach(btn => {
			btn.classList.toggle('mm-active', parseInt(btn.dataset.gridToggle) === cols);
		});
	},

	_apply(cols) {
		const grid = document.querySelector('[data-collection-grid]');
		if (!grid) return;

		const items = Array.from(grid.children);

		// FLIP — First: record positions before change
		const first = items.map(el => el.getBoundingClientRect());

		// Apply new grid
		grid.dataset.gridCols = cols;

		// FLIP — Last: record positions after change, then Invert + Play
		items.forEach((el, i) => {
			const last = el.getBoundingClientRect();
			const dx = first[i].left - last.left;
			const dy = first[i].top - last.top;
			const sx = first[i].width / last.width;
			const sy = first[i].height / last.height;

			if (dx === 0 && dy === 0 && sx === 1 && sy === 1) return;

			el.style.transformOrigin = 'top left';
			el.style.transition = 'none';
			el.style.transform = `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;

			requestAnimationFrame(() => {
				el.style.transition = 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)';
				el.style.transform = '';
				el.addEventListener('transitionend', () => {
					el.style.transition = '';
					el.style.transform = '';
					el.style.transformOrigin = '';
				}, { once: true });
			});
		});

		document.querySelectorAll('[data-grid-toggle]').forEach(btn => {
			btn.classList.toggle('mm-active', parseInt(btn.dataset.gridToggle) === cols);
		});
	}
};

/*********************************************************************************************
 * 🛏️ EXPORTED FUNCTIONS (for backward compatibility)
 */
window.mmSortByChanged = (select) => {
	const hiddenSelect = CollectionDOM.sortByHidden;
	if (!hiddenSelect || !select) {
		collectionEmit('sort-error', { error: 'Sort by elements not found' });
		return;
	}
	
	hiddenSelect.value = select.value;
	CollectionFilters.submitForm();
};

// Mobile filter functions
window.mmOpenFiltersMobile = () => MobileFilters.open();
window.mmCloseFiltersMobile = () => MobileFilters.close();
window.mmCheckDrawer = () => MobileFilters.checkDrawer();

/*********************************************************************************************
 * 🚀 INITIALIZATION
 */
document.addEventListener('DOMContentLoaded', () => {
	collectionEmit('init-start');
	
	try {
		CollectionFilters.init();
		ColorGroupFilters.init();
		ColorStripToggle.init();
		GridToggle.init();
		FilterAccordions.init();
		PriceSliders.init();
		SortBy.init();
		SortDropdown.init();
		BreakingBlockParallax.init();
		MobileFilters.checkDrawer();
		
		collectionEmit('init-success');
	} catch (error) {
		collectionEmit('init-error', { error: error.message });
		window.log('error', 'Collection', 'Collection initialization failed', { error: error.message });
	}
});

/*********************************************************************************************
 * 🎯 ACCESSIBILITY - Event Listeners (replace inline onclick)
 */
document.addEventListener('DOMContentLoaded', () => {
	// Filter close (button + overlay)
	document.querySelectorAll('[data-filter-close]').forEach(el => {
		el.addEventListener('click', () => {
			if (typeof window.mmCloseFiltersMobile === 'function') {
				window.mmCloseFiltersMobile();
			}
		});
	});

	// Filter submit button
	const filterSubmitBtn = document.querySelector('[data-filter-submit]');
	if (filterSubmitBtn) {
		filterSubmitBtn.addEventListener('click', () => {
			CollectionFilters.submitForm();
		});
	}

	// Clear all filters button
	const filterClearBtn = document.querySelector('[data-active-filters]');
	if (filterClearBtn) {
		filterClearBtn.addEventListener('click', () => {
			const form = CollectionDOM.form;
			if (!form) return;
			form.querySelectorAll('input[type="checkbox"]').forEach(cb => { cb.checked = false; });
			form.querySelectorAll('input[type="range"]').forEach(range => {
				range.value = range.name.includes('gte') ? range.min : range.max;
			});
			CollectionFilters.submitForm();
		});
	}

	// Open filters mobile buttons
	const openFiltersBtns = document.querySelectorAll('[data-open-filters-mobile]');
	openFiltersBtns.forEach((btn) => {
		btn.addEventListener('click', () => {
			if (typeof window.mmCloseMobileMenu === 'function') {
				window.mmCloseMobileMenu();
			}
			if (typeof window.mmOpenFiltersMobile === 'function') {
				window.mmOpenFiltersMobile();
			}
		});
	});

	/* Load more produits (pagination grille) */
	CollectionLoadMore.init();
});

/*********************************************************************************************
 * 🔧 DEVELOPMENT LOGGING
 */
if (window.isDev) {
	['init-success', 'filter-success', 'input-change', 'form-submit-start', 'price-update', 'sort-change', 'filter-preview-start', 'filter-preview-success', 'filter-preview-error'].forEach(event => {
		document.addEventListener(`collection:${event}`, e =>
			console.log(`🏷️ [COLLECTION] ${event}:`, e.detail)
		);
	});
}